/**
 * Mirrors apps/backend/core/constants.py::CLASSROOM_ICONS + CLASSROOM_DEFAULT_ICONS.
 * Canonical source is the backend allowlist; keep this in sync manually
 * (per magic-values.md §1, dry-principles.md §5). When you add/remove an
 * icon name on the backend, mirror it here in the same PR.
 *
 * Named lucide-react imports only (tree-shakeable per frontend-performance.md
 * §18) — NEVER `import * as Icons`.
 */

import {
  Activity,
  Apple,
  Atom,
  Book,
  BookOpen,
  Brush,
  Calculator,
  Code,
  Cpu,
  Dna,
  Drama,
  Dumbbell,
  FlaskConical,
  Globe,
  GraduationCap,
  HeartPulse,
  Landmark,
  Languages,
  Laptop,
  Leaf,
  Library,
  Lightbulb,
  Map,
  Microscope,
  Music,
  Palette,
  PenTool,
  Ruler,
  Scale,
  Shapes,
  Sigma,
  Sparkles,
  Star,
  TestTube,
  Trophy,
  Users,
  Wrench,
  type LucideIcon,
} from "lucide-react"

/**
 * The exact allowlist of selectable classroom icon names, ordered GROUPED
 * (by school domain) so the picker renders a coherent matrix. Mirrors
 * core/constants.py::CLASSROOM_ICONS.
 */
export const CLASSROOM_ICON_NAMES = [
  // Ciencias
  "FlaskConical",
  "Atom",
  "Microscope",
  "Dna",
  "TestTube",
  "Leaf",
  // Matemáticas
  "Calculator",
  "Ruler",
  "Sigma",
  "Shapes",
  // Lengua y letras
  "BookOpen",
  "Book",
  "Library",
  "PenTool",
  "Languages",
  "Apple",
  // Sociales
  "Landmark",
  "Globe",
  "Map",
  "Scale",
  "Users",
  // Artes y música
  "Palette",
  "Music",
  "Brush",
  "Drama",
  // Salud y deporte
  "Dumbbell",
  "Activity",
  "HeartPulse",
  "Trophy",
  // Tecnología
  "Cpu",
  "Code",
  "Laptop",
  "Wrench",
  // Genéricas
  "GraduationCap",
  "Star",
  "Lightbulb",
  "Sparkles",
] as const

export type ClassroomIconName = (typeof CLASSROOM_ICON_NAMES)[number]

/**
 * Name → lucide component registry. Resolves an `effective_icon` string to a
 * renderable component. `iconComponentFor` falls back to GraduationCap for any
 * unknown name (forward-compat if the backend ships an icon this bundle
 * predates).
 */
export const CLASSROOM_ICON_REGISTRY: Record<string, LucideIcon> = {
  FlaskConical,
  Atom,
  Microscope,
  Dna,
  TestTube,
  Leaf,
  Calculator,
  Ruler,
  Sigma,
  Shapes,
  BookOpen,
  Book,
  Library,
  PenTool,
  Languages,
  Apple,
  Landmark,
  Globe,
  Map,
  Scale,
  Users,
  Palette,
  Music,
  Brush,
  Drama,
  Dumbbell,
  Activity,
  HeartPulse,
  Trophy,
  Cpu,
  Code,
  Laptop,
  Wrench,
  GraduationCap,
  Star,
  Lightbulb,
  Sparkles,
}

/**
 * Subject `default_code` → default icon name. Mirrors
 * core/constants.py::CLASSROOM_DEFAULT_ICONS — covers the 10 seeded default
 * subjects (PRD-37 / ADR-0031). A school-custom subject has
 * `default_code: null` and falls through to the "unknown key" fallback below.
 */
export const SUBJECT_DEFAULT_ICONS: Record<string, string> = {
  matematicas: "Calculator",
  ciencias_naturales: "FlaskConical",
  historia: "Landmark",
  espanol: "BookOpen",
  geografia: "Globe",
  formacion_civica: "Scale",
  educacion_fisica: "Dumbbell",
  artes: "Palette",
  ingles: "Languages",
  tecnologia: "Cpu",
}

/** Neutral fallback tint when the subject is unknown. */
/**
 * PRD-59 — the per-subject accents below stay raw hues. They are a CATEGORICAL
 * recognition scale ("Ciencias is green-ish, Historia is amber-ish"), and the
 * token layer names five roles: an amber subject is not a warning. Same
 * reasoning as `constants/avatarTones.ts` and the adaptive-level ramp. The
 * neutral fallback does use a role, because "no subject" genuinely is neutral.
 */
const NEUTRAL_ACCENT = "text-ink-muted"

/**
 * Subject value → tailwind text-color class used to tint the classroom icon.
 * Frontend-only deterministic accent (no DB field) — keeps the
 * "Ciencias is green-ish, Historia is amber-ish" recognition without a color
 * picker (PRD-30 §UX). Each pairs a 600 light shade with a 400 dark shade for
 * AA contrast on both themes.
 */
export const SUBJECT_ICON_ACCENT: Record<string, string> = {
  matematicas: "text-indigo-600 dark:text-indigo-400",
  ciencias_naturales: "text-emerald-600 dark:text-emerald-400",
  historia: "text-amber-600 dark:text-amber-400",
  espanol: "text-rose-600 dark:text-rose-400",
  geografia: "text-sky-600 dark:text-sky-400",
  formacion_civica: "text-violet-600 dark:text-violet-400",
  educacion_fisica: "text-orange-600 dark:text-orange-400",
  artes: "text-fuchsia-600 dark:text-fuchsia-400",
  ingles: "text-cyan-600 dark:text-cyan-400",
  tecnologia: NEUTRAL_ACCENT,
}

/** The default icon name for a subject, or GraduationCap when unmapped. */
export function defaultIconForSubject(subject: string): string {
  return SUBJECT_DEFAULT_ICONS[subject] ?? "GraduationCap"
}

/** Resolve an icon name to its component, GraduationCap fallback for unknowns. */
export function iconComponentFor(name: string): LucideIcon {
  return CLASSROOM_ICON_REGISTRY[name] ?? GraduationCap
}

/** The accent tint class for a subject, neutral fallback when unmapped. */
export function accentForSubject(subject: string): string {
  return SUBJECT_ICON_ACCENT[subject] ?? NEUTRAL_ACCENT
}
